// src/components/Login.tsx
import React, { useState, useEffect } from 'react';
import { login, getCurrentUser, backendPublicOrigin } from '../../services/api';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser, logoutUser } from '../../store/authSlice';
import { User } from '../../types';
import { FaSpinner } from 'react-icons/fa';

interface FormData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await getCurrentUser();
        if (res) {
            console.log("Current user found:", res);
            dispatch(setUser(res));
            setCurrentUser(res);
        } else {
            // Explicitly set to null when no user data
            console.log("No current user session found");
            setCurrentUser(null);
        }
      } catch (err) {
        console.log("ERROR during login check:", err);
        // Explicitly set to null on error
        setCurrentUser(null);
      }
    };
    fetchUser();
  }, [dispatch]);

  const [formData, setFormData] = useState<FormData>({ email: '', password: '' });
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      console.log('🔐 Login component: Starting login process');
      const res = await login(formData);
      console.log("🔐 Login component response:", res);
      console.log("🔐 Login component user data:", res?.user);
      console.log("🔐 Login component token:", res?.token ? 'present' : 'missing');

      setMessage(res.message);
      dispatch(setUser(res.user)); // Dispatch the user to the Redux store
      console.log('🔐 Login component: User dispatched to Redux, navigating to dashboard');
      navigate('/dashboard'); // Redirect to dashboard after successful login
    } catch (err: any) {
        console.log('❌ Login component error:', err);
        if (!err.response || err.code === "ERR_NETWORK" ) {
          // Network error
          setMessage('Network or server error. Please check your connection and server.');
        } else {
            setMessage(err.response?.data?.message || `Login failed (${err.code})`);
        }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = () => {
    window.location.href = `${backendPublicOrigin()}/auth/google`;
  };

  const handleGithub = () => {
    window.location.href = `${backendPublicOrigin()}/auth/github`;
  };

  const handleLogout = () => {
    dispatch(logoutUser()); // Logout the current user
    setCurrentUser(null);
    setMessage("Logged out successfully");
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        <h2 className="text-3xl font-semibold text-center mb-6 text-blue-600">
          {currentUser ? 'Already logged in' : 'Login'}
        </h2>

        {location.search.includes('verified=true') && (
          <p className="text-center text-green-500 mb-4">
            Your email has been verified. Please log in.
          </p>
        )}

        {message && <p className="text-center text-red-500 mb-4">{message}</p>}

        {/* If the user is already logged in, show Relogin view */}
        {currentUser && currentUser.email ? (

            <div className="flex flex-col items-center">
                <p className="text-center text-gray-700 mb-4">
                You are currently logged in as <strong>{currentUser.email}</strong>
                {currentUser.method && ` (${currentUser.method})`}.
                </p>
                <button
                onClick={() => navigate('/dashboard')}  // Navigate to dashboard
                className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition duration-300 mb-4"
                >
                Go to Dashboard
                </button>
                <h2 className="text-3xl font-semibold text-center mb-6 text-blue-600">____________</h2>

                <h2 className="text-3xl font-semibold text-center mb-6 text-blue-600">
                Relogin (as different user)
                </h2>
                <button
                onClick={handleLogout}
                className="w-full bg-yellow-600 text-white py-2 rounded-lg hover:bg-yellow-700 transition duration-300"
                >
                Logout and re-login
                </button>
            </div>

        ) : (
          // If no user is logged in, show the normal login form
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </button>
            </form>

            <hr className="my-6" />

            <div className="flex justify-between">
              <button
                onClick={handleGoogle}
                className="w-5/12 bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition duration-300"
              >
                Google
              </button>
              <button
                onClick={handleGithub}
                className="w-5/12 bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-900 transition duration-300"
              >
                GitHub
              </button>
            </div>

            <p className="mt-6 text-center text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-600 hover:underline">
                Register
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
